/**
 * LevelService — the front-end's interface to the level-sharing backend (Brief
 * 008, steps 1–2). One small interface, two transports:
 *
 *   • HttpLevelService  — talks to the REST API on the VPS (production).
 *   • MockLevelService  — localStorage-backed faithful stand-in (dev/test, no infra).
 *
 * `getLevelService()` picks the transport from config (env). The editor's Publish
 * button and the in-game Browse screen depend only on the LevelService interface,
 * so they're built and tested against the mock and go live by flipping env — no
 * UI change. See docs/SHARING_API.md for the wire contract.
 *
 * Both transports share `prepareForPublish()` so the mock rejects EXACTLY like the
 * server (same validator, same content id, same size cap) — the local test bed is
 * faithful, not a happy-path fake.
 */
import { computeLevelId, validateLevel, type LevelFormat } from '../engine/LevelFormat';
import type { Replay } from '../replay/Replay';
import { API_ROOT, LIMITS, REQUEST_TIMEOUT_MS, SHARING_MODE } from './config';
import { MockLevelService } from './MockLevelService';
import { forgetOwnerToken, getOwnerToken, rememberOwnerToken } from './ownerTokens';
import type {
  DailyResponse, DeathPoint, ForksResponse, HeatmapResponse, LevelListResponse, ListParams,
  PublishResponse, PublishedLevel, RunDetail, RunListParams, RunListResponse, SubmitRunRequest,
  SubmitRunResponse,
} from './types';

// ── Errors ────────────────────────────────────────────────────────────────────

export type SharingErrorCode =
  | 'invalid'       // local validation failed — request was NOT sent
  | 'too-large'     // payload over the size cap
  | 'network'       // fetch failed / timeout / CORS / DNS
  | 'rate-limited'  // server 429
  | 'not-found'     // server 404
  | 'conflict'      // server 409
  | 'server'        // server 5xx / unexpected status
  | 'bad-response'; // 2xx but body wasn't the expected JSON

/** Typed error every LevelService method rejects with — UI switches on `.code`. */
export class SharingError extends Error {
  constructor(readonly code: SharingErrorCode, message: string, readonly detail?: unknown) {
    super(message);
    this.name = 'SharingError';
  }
}

// ── Interface ───────────────────────────────────────────────────────────────--

export interface LevelService {
  /** Publish a level. Re-validates + stamps levelId locally first (fails fast).
   *  `thumbnail` (PNG data URL) is optional library art. `clearRun` (POLISH_04 #2) is
   *  an optional author-recorded clear replay; the server re-sims it and, if it
   *  verifies, makes the level eligible for the daily-challenge pool. */
  publish(level: LevelFormat, opts?: { cleared?: boolean; thumbnail?: string; clearRun?: Replay }): Promise<PublishResponse>;
  /** Browse published levels (paginated, sortable, searchable). */
  list(params?: ListParams): Promise<LevelListResponse>;
  /** Fetch one published level WITH its full playable payload. */
  get(id: string): Promise<PublishedLevel>;
  /** Best-effort play-count bump. Resolves even on failure (never blocks play). */
  recordPlay(id: string): Promise<void>;

  // ── UGC lifecycle (POLISH_04 #1) ──────────────────────────────────────────────
  /** Author unpublish. Requires the owner token issued on publish (looked up via
   *  `ownerTokens.ts` by callers that don't hold one directly); rejects with
   *  SharingError('invalid') if this browser never published (or forgot) that level.
   *  On success, forgets the local token (the server record is gone). */
  unpublish(levelPubId: string): Promise<void>;
  /** File a report against a published level. Never throws for a "normal" reject —
   *  UI should treat this as fire-and-forget-ish, but it DOES reject on network/5xx so
   *  a caller can retry, unlike the best-effort analytics pings above. */
  report(levelPubId: string, reason?: string): Promise<void>;

  // ── Leaderboards (Tier 1, Phase B) ────────────────────────────────────────────
  /** Submit a recorded run to a level's board. `levelPubId` is the per-upload id (the
   *  one play/browse use). The server reads the time from the replay + verifies it. */
  submitRun(levelPubId: string, replay: Replay, author: string): Promise<SubmitRunResponse>;
  /** Top runs for a level, by time ascending (summaries only — no replay blob). */
  getRuns(levelPubId: string, params?: RunListParams): Promise<RunListResponse>;
  /** Fetch one run WITH its replay payload, for ghost racing. */
  getReplay(runId: string): Promise<RunDetail>;

  // ── Daily challenge (Tier 1, Phase D) ─────────────────────────────────────────
  /** Today's challenge level (one published level per UTC day) + its board key. */
  getDaily(): Promise<DailyResponse>;

  // ── Fork / lineage (Tier 2, Feature E) ────────────────────────────────────────
  /** Direct children: levels that list this level's content as their parent. */
  getForks(levelPubId: string): Promise<ForksResponse>;

  // ── Death heatmap + clear-rate (Tier 2, Feature F) ────────────────────────────
  /** Best-effort: report a batch of death cells for a played level. Resolves even on
   *  failure (never blocks play, mirrors recordPlay). The transport caps the batch. */
  submitDeaths(levelPubId: string, points: DeathPoint[]): Promise<void>;
  /** Best-effort: count one confirmed clear (clearRate numerator). Resolves on failure. */
  recordClear(levelPubId: string): Promise<void>;
  /** Aggregated death cells + play/clear totals for the creator heatmap view. */
  getHeatmap(levelPubId: string): Promise<HeatmapResponse>;
}

// ── Shared publish pre-flight (used by BOTH transports) ─────────────────────────

/**
 * Validate + stamp + size-check a level before publish. Reuses the engine's
 * canonical `validateLevel` and `computeLevelId` so client, mock, and server agree
 * bit-for-bit. Returns a CLONE with `meta.levelId` stamped + its byte size.
 * Throws SharingError('invalid' | 'too-large').
 */
export function prepareForPublish(level: LevelFormat): { level: LevelFormat; levelId: string; bytes: number } {
  const out = structuredClone(level);
  const errs = validateLevel(out);
  if (errs.length) throw new SharingError('invalid', `level failed validation: ${errs.join('; ')}`, errs);

  const levelId = computeLevelId(out);
  out.meta.levelId = levelId;

  const bytes = new TextEncoder().encode(JSON.stringify(out)).length;
  if (bytes > LIMITS.maxLevelBytes) {
    throw new SharingError('too-large', `level is ${bytes} bytes (max ${LIMITS.maxLevelBytes})`, { bytes });
  }
  return { level: out, levelId, bytes };
}

// ── HTTP transport ──────────────────────────────────────────────────────────--

function errorForStatus(status: number, bodyText: string): SharingError {
  const detail = bodyText.slice(0, 500);
  switch (status) {
    case 400: return new SharingError('invalid', 'server rejected the level (400)', detail);
    case 404: return new SharingError('not-found', 'level not found (404)', detail);
    case 409: return new SharingError('conflict', 'conflict (409)', detail);
    case 413: return new SharingError('too-large', 'payload too large (413)', detail);
    case 429: return new SharingError('rate-limited', 'too many requests — slow down (429)', detail);
    default:
      return new SharingError(status >= 500 ? 'server' : 'server', `unexpected server status ${status}`, detail);
  }
}

async function safeText(res: Response): Promise<string> {
  try { return await res.text(); } catch { return ''; }
}

/** Absolute thumbnail URL for the http transport, or undefined when none exists. */
function thumbUrlFor(id: string, hasThumb?: boolean): string | undefined {
  return hasThumb ? `${API_ROOT}/levels/${encodeURIComponent(id)}/thumb` : undefined;
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${API_ROOT}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (e) {
    throw new SharingError('network', 'could not reach the sharing server', e);
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) throw errorForStatus(res.status, await safeText(res));
  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch (e) {
    throw new SharingError('bad-response', 'server returned malformed JSON', e);
  }
}

class HttpLevelService implements LevelService {
  async publish(level: LevelFormat, opts?: { cleared?: boolean; thumbnail?: string; clearRun?: Replay }): Promise<PublishResponse> {
    const { level: prepared } = prepareForPublish(level);
    const body = JSON.stringify({
      level: prepared, cleared: opts?.cleared ?? false, thumbnail: opts?.thumbnail, clearRun: opts?.clearRun,
    });
    const res = await http<PublishResponse>('/levels', { method: 'POST', body });
    // POLISH_04 #1 — the server only ever hands back ownerToken once (a fresh publish);
    // persist it now or a later unpublish has no capability to present.
    if (res.ownerToken) rememberOwnerToken(res.id, res.ownerToken);
    return res;
  }

  async unpublish(levelPubId: string): Promise<void> {
    const token = getOwnerToken(levelPubId);
    if (!token) throw new SharingError('invalid', 'no owner token for this level (not published from this browser?)');
    await http<void>(`/levels/${encodeURIComponent(levelPubId)}`, {
      method: 'DELETE', headers: { 'X-Owner-Token': token },
    });
    forgetOwnerToken(levelPubId);
  }

  async report(levelPubId: string, reason?: string): Promise<void> {
    await http<void>(`/levels/${encodeURIComponent(levelPubId)}/report`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? '' }),
    });
  }

  async list(params?: ListParams): Promise<LevelListResponse> {
    const qs = new URLSearchParams();
    if (params?.sort) qs.set('sort', params.sort);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await http<LevelListResponse>(`/levels${suffix}`);
    res.items.forEach((it) => { it.thumbUrl = thumbUrlFor(it.id, it.hasThumb); });
    return res;
  }

  async get(id: string): Promise<PublishedLevel> {
    const lvl = await http<PublishedLevel>(`/levels/${encodeURIComponent(id)}`);
    lvl.thumbUrl = thumbUrlFor(lvl.id, lvl.hasThumb);
    return lvl;
  }

  async recordPlay(id: string): Promise<void> {
    // Best-effort: never let a failed analytics ping block the player.
    try { await http<void>(`/levels/${encodeURIComponent(id)}/play`, { method: 'POST' }); }
    catch { /* swallowed by design */ }
  }

  async submitRun(levelPubId: string, replay: Replay, author: string): Promise<SubmitRunResponse> {
    const body = JSON.stringify({ author, replay } satisfies SubmitRunRequest);
    return http<SubmitRunResponse>(`/levels/${encodeURIComponent(levelPubId)}/runs`, { method: 'POST', body });
  }

  async getRuns(levelPubId: string, params?: RunListParams): Promise<RunListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    // Tier 3 (Feature H): always pin the category so the board never mixes — the mutator
    // set (0 = vanilla) AND the authored archetype ('classic' = default).
    qs.set('mutatorSetId', String(params?.mutatorSetId ?? 0));
    qs.set('archetype', params?.archetype ?? 'classic');
    return http<RunListResponse>(`/levels/${encodeURIComponent(levelPubId)}/runs?${qs.toString()}`);
  }

  async getReplay(runId: string): Promise<RunDetail> {
    return http<RunDetail>(`/runs/${encodeURIComponent(runId)}`);
  }

  async getDaily(): Promise<DailyResponse> {
    return http<DailyResponse>('/daily');
  }

  async getForks(levelPubId: string): Promise<ForksResponse> {
    return http<ForksResponse>(`/levels/${encodeURIComponent(levelPubId)}/forks`);
  }

  async submitDeaths(levelPubId: string, points: DeathPoint[]): Promise<void> {
    if (!points.length) return;
    const capped = points.slice(0, LIMITS.maxDeathsPerSubmit);
    // Best-effort analytics: swallow ALL failures (offline / 404 on a server that
    // predates Tier 2 / CORS) so a death ping never surfaces to the player.
    try {
      await http<void>(`/levels/${encodeURIComponent(levelPubId)}/deaths`, {
        method: 'POST', body: JSON.stringify({ points: capped }),
      });
    } catch { /* swallowed by design */ }
  }

  async recordClear(levelPubId: string): Promise<void> {
    try { await http<void>(`/levels/${encodeURIComponent(levelPubId)}/clear`, { method: 'POST' }); }
    catch { /* swallowed by design */ }
  }

  async getHeatmap(levelPubId: string): Promise<HeatmapResponse> {
    return http<HeatmapResponse>(`/levels/${encodeURIComponent(levelPubId)}/heatmap`);
  }
}

// ── Factory ───────────────────────────────────────────────────────────────────

let singleton: LevelService | null = null;

/** The configured LevelService (singleton). Transport chosen from env at first call. */
export function getLevelService(): LevelService {
  if (!singleton) singleton = SHARING_MODE === 'http' ? new HttpLevelService() : new MockLevelService();
  return singleton;
}
